import React, { useState, useEffect } from 'react';
import './AddressSelector.css';
import api from '../../api';
import { Address } from '../../types';
import Loader from '../../components/Loader';
import { INDIAN_STATES } from '../../utils/indianStates';
import { INDIAN_CITIES_BY_STATE } from '../../utils/indianCities';

interface AddressSelectorProps {
  onSelect: (addressId: number | null | undefined) => void;
  selectedId: number | null | undefined;
}

// Type for Indian states to ensure type safety
type IndianState = keyof typeof INDIAN_CITIES_BY_STATE;

// We use Omit to tell TS that the form doesn't have an ID yet
type AddressForm = Omit<Address, 'id'> & {
  state: IndianState | '';
};

const AddressSelector: React.FC<AddressSelectorProps> = ({ onSelect, selectedId }) => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showAll, setShowAll] = useState<boolean>(false);
  const [isAdding, setIsAdding] = useState<boolean>(false);

  const [formData, setFormData] = useState<AddressForm>({
    name: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    pincode: ''
  });

// Helper function to safely get cities for a state
const getCitiesForState = (state: string): string[] => {
  if (state && state in INDIAN_CITIES_BY_STATE) {
    return INDIAN_CITIES_BY_STATE[state as IndianState];
  }
  return [];
};

// Get available cities based on selected state
const availableCities = getCitiesForState(formData.state);

  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        setLoading(true);
        const data: Address[] = await api.getAllAddresses();
        setAddresses(data);
        
        if (data.length > 0 && (selectedId === null || selectedId === undefined)) {
          onSelect(data[0].id);
        }
      } catch (error) {
        console.error("Fetch Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAddresses();
  }, [onSelect, selectedId]);

  const handleSaveAddress = async () => {
    const { name, phone, street, city, state, pincode } = formData;
    if (!name || !phone || !street || !city || !state || !pincode) {
      alert("All fields are required.");
      return;
    }

    try {
      const newAddress: Address = await api.createAddresses(formData as any);
      setAddresses((prev) => [...prev, newAddress]);
      onSelect(newAddress.id);
      setIsAdding(false);
      setFormData({ name: '', phone: '', street: '', city: '', state: '', pincode: '' });
    } catch (error) {
      alert("Address creation failed.");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, state: value as IndianState | '', city: '' })); // Reset city when state changes
  };

  const selectedAddress = addresses.find((a) => a.id === selectedId);

  if (loading) return (
    <div className="loading-center">
      <Loader size="medium" type="spinner" text="Loading addresses..." />
    </div>
  );

  return (
    <div className="address-selector-wrapper">
      <div className="address-header-section">
        <h2>Delivery Address</h2>
      </div>

      {!isAdding ? (
        <>
          {/* RECOVERED: Selected Address Card UI */}
          {selectedAddress && (
            <div className="selected-address-card">
              <div className="card-badge">Current</div>
              <div className="address-content">
                <h3>{selectedAddress.name}</h3>
                <p>{selectedAddress.street}, {selectedAddress.city}</p>
                <p className="phone-line"><strong>Phone:</strong> {selectedAddress.phone}</p>
              </div>
            </div>
          )}

          <button className="toggle-list-btn" onClick={() => setShowAll(!showAll)}>
            {showAll ? 'Collapse' : 'Change Address'}
          </button>

          {/* RECOVERED: Vertical Stack List UI */}
          {showAll && (
            <div className="address-vertical-stack">
              {addresses
                .filter(a => a.id !== selectedId) // Hide the currently selected one from the list
                .map(addr => (
                  <div key={addr.id} className="address-row-item" onClick={() => { onSelect(addr.id); setShowAll(false); }}>
                    <div className="item-meta">
                      <h4>{addr.name}</h4>
                      <p>{addr.street}, {addr.city}</p>
                    </div>
                    <div className="select-arrow">Select →</div>
                  </div>
                ))}
            </div>
          )}

          <button className="add-new-address-btn" onClick={() => setIsAdding(true)}>
            + Add New Address
          </button>
        </>
      ) : (
        <div className="address-form-card">
          <div className="form-header">New Address</div>
          <div className="form-grid">
            <input type="text" name="name" placeholder="Full Name" className="form-input" value={formData.name} onChange={handleInputChange} />
            <input type="text" name="phone" placeholder="Phone Number" className="form-input" value={formData.phone} onChange={handleInputChange} />
            <input type="text" name="street" placeholder="Street / House No." className="form-input full" value={formData.street} onChange={handleInputChange} />
            <select name="state" className="form-select" value={formData.state} onChange={handleStateChange}>
              <option value="">Select State</option>
              {INDIAN_STATES.map((state) => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
            <select name="city" className="form-select" value={formData.city} onChange={handleInputChange} disabled={!formData.state}>
              <option value="">Select City</option>
              {availableCities.map((city: string) => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
            <input type="text" name="pincode" placeholder="Pincode" className="form-input" value={formData.pincode} onChange={handleInputChange} />
          </div>
          <div className="form-actions">
            <button className="cancel-btn" onClick={() => setIsAdding(false)}>Cancel</button>
            <button className="save-btn" onClick={handleSaveAddress}>Save Address</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressSelector;